import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Button,
  Divider,
  useTheme,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CreditCardOffIcon from '@mui/icons-material/CreditCardOff';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import EventNoteIcon from '@mui/icons-material/EventNote';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import InsightsIcon from '@mui/icons-material/Insights';
import { useLanguage } from '../contexts/LanguageContext';
import goldColors from '../themes/goldColors';

// 17 sep 2026 (Augusto: "Necesito completar el ABOUT US"): esta página era
// un placeholder literal -- decía "Contenido en Desarrollo" y prometía que
// la información "será proporcionada próximamente". Peor que estar vacía:
// un visitante que llega a Sobre Nosotros está evaluando si confiarle sus
// finanzas a un desconocido, y encontraba un cartel de obra.
//
// Voz elegida por Augusto: PERSONAL, con nombre y cara. Es un producto
// joven de una sola persona; escribir "nuestro equipo de expertos" sonaría
// inflado y cualquiera lo nota. Su historia real es el activo más difícil
// de copiar que tiene.
//
// 17 sep 2026, corrección de Augusto: "ojo el about us se distancia de PCM
// dado que este app es solo para finanzas personales". La primera versión
// describía el problema desde la óptica del DUEÑO DE NEGOCIO (los 4
// síntomas de su metodología de consultoría: no puedes crecer, costos
// inflados...) -- justo después de que sacamos los planes de empresa de
// PCM y mandamos a las empresas a CapitalOptima. Un visitante habría
// entendido que PCM sirve para su negocio.
//
// Ahora la historia de consultoría queda como CREDENCIAL (por qué sabe de
// esto), no como descripción del cliente, y los síntomas son de finanzas
// personales. Se agregó además un bloque explícito de límite: PCM es para
// tu plata, no la de tu empresa.
//
// El contenido no es inventado: la credencial y la filosofía salen de su
// documentación real de Darwin Optima en Notion. No se afirma nada que no
// sea verificable: ni número de clientes, ni años en el mercado, ni tamaño
// de equipo.

const makeSintomas = (isSpanish) => [
  {
    icon: VisibilityOffIcon,
    titulo: isSpanish ? 'No sabes cuánto tienes de verdad' : "You don't really know how much you have",
    desc: isSpanish
      ? 'Algo en la cuenta de ahorros, algo en la corriente, algo en efectivo, y una tarjeta que ya usaste pero todavía no llega. La suma nunca está en un solo lugar.'
      : 'Some in savings, some in checking, some in cash, and a card you already used but hasn’t posted yet. The total is never in one place.',
  },
  {
    icon: HelpOutlineIcon,
    titulo: isSpanish ? 'No sabes en qué se te va' : "You don't know where it goes",
    desc: isSpanish
      ? 'No fue un gasto grande. Fueron cuarenta chicos que nadie anotó. A fin de mes falta y no hay a quién culpar.'
      : 'It wasn’t one big expense. It was forty small ones nobody wrote down. At month-end it’s short and there’s nobody to blame.',
  },
  {
    icon: CreditCardOffIcon,
    titulo: isSpanish ? 'Las cuotas te sorprenden' : 'Payments catch you off guard',
    desc: isSpanish
      ? 'La tarjeta, el préstamo del auto, la cuota del colegio. Cada una te acuerdas el día que vence, nunca la semana antes, que es cuando servía.'
      : 'The card, the car loan, the school fee. You remember each one the day it’s due, never the week before, which is when it would have helped.',
  },
  {
    icon: HourglassBottomIcon,
    titulo: isSpanish ? 'No sabes si llegas a fin de mes' : "You don't know if you'll make it to month-end",
    desc: isSpanish
      ? 'Y cuando lo averiguas ya es el día 28, cuando la única salida que queda es la tarjeta.'
      : 'And by the time you find out it’s the 28th, when the only way out left is the credit card.',
  },
];

const makePilares = (isSpanish) => [
  {
    icon: EventNoteIcon,
    letra: 'P',
    titulo: isSpanish ? 'Planificación' : 'Planning',
    desc: isSpanish
      ? 'Antes de que el mes ocurra: qué entra, qué sale, qué cuotas vencen y con cuánto te quedas. Si no alcanza, te enteras ahora y no el día 28.'
      : "Before the month happens: what comes in, what goes out, which payments are due and what you're left with. If it doesn't add up, you find out now and not on the 28th.",
  },
  {
    icon: FactCheckIcon,
    letra: 'C',
    titulo: isSpanish ? 'Control' : 'Control',
    desc: isSpanish
      ? 'Registrar sin que se vuelva una tarea: a mano, por voz, por WhatsApp o importando el estado de cuenta del banco. Lo que no se registra, no se controla.'
      : 'Recording without it becoming a chore: by hand, by voice, over WhatsApp, or by importing your bank statement. What does not get recorded does not get controlled.',
  },
  {
    icon: InsightsIcon,
    letra: 'M',
    titulo: isSpanish ? 'Monitoreo' : 'Monitoring',
    desc: isSpanish
      ? 'Mirar hacia atrás con honestidad: en qué se fue el dinero, cómo se movió tu deuda, si ahorraste o gastaste de más. Sin proyecciones inventadas, con tus números reales.'
      : 'Looking back honestly: where the money went, how your debt moved, whether you saved or overspent. No made-up projections, just your real numbers.',
  },
];

const NosotrosPage = () => {
  const theme = useTheme();
  const { language } = useLanguage();
  const isSpanish = language === 'es';
  const sintomas = makeSintomas(isSpanish);
  const pilares = makePilares(isSpanish);

  return (
    <Box sx={{ py: { xs: 5, md: 8 } }}>
      <Container maxWidth="lg">
        {/* Encabezado */}
        <Box sx={{ textAlign: 'center', mb: 7 }}>
          <Typography
            variant="h3"
            component="h1"
            gutterBottom
            fontWeight="bold"
            sx={{
              background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${goldColors.main})`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 2,
            }}
          >
            {isSpanish ? 'Sobre Nosotros' : 'About Us'}
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 780, mx: 'auto', mb: 2 }}>
            {isSpanish
              ? 'PCM es para personas y familias que quieren ordenar su plata, llegar bien a fin de mes y dejar de cargar con la duda de no saber cómo van.'
              : "PCM is for people and families who want to get their money in order, reach the end of the month comfortably, and stop carrying the weight of not knowing where they stand."}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 700, mx: 'auto' }}>
            {isSpanish
              ? 'Casi nunca es un problema de cuánto ganas. Es un problema de no ver: cuando todo está a la vista, la mayor parte de la angustia se va sola.'
              : "It's almost never about how much you earn. It's about not seeing: when everything is in plain sight, most of the anxiety goes away on its own."}
          </Typography>
        </Box>

        {/* Los 4 síntomas */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h5" gutterBottom fontWeight="bold" align="center">
            {isSpanish ? 'Si te reconoces en esto, PCM es para ti' : 'If you recognize yourself here, PCM is for you'}
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center" sx={{ maxWidth: 700, mx: 'auto', mb: 4 }}>
            {isSpanish
              ? 'Le pasa igual a quien vive solo que a quien mantiene una casa entera. Y el desgaste no viene del monto: viene de la incertidumbre.'
              : "It happens the same to someone living alone as to someone supporting a whole household. And the wear doesn't come from the amount: it comes from the uncertainty."}
          </Typography>
          <Grid container spacing={3}>
            {sintomas.map((s) => {
              const Icon = s.icon;
              return (
                <Grid item xs={12} sm={6} key={s.titulo}>
                  <Paper variant="outlined" sx={{ p: 3, height: '100%', borderRadius: 2 }}>
                    <Icon sx={{ color: goldColors.main, mb: 1 }} />
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                      {s.titulo}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {s.desc}
                    </Typography>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </Box>

        {/* Quién está detrás */}
        <Paper elevation={2} sx={{ p: { xs: 3, md: 5 }, mb: 6, borderRadius: 2 }}>
          <Typography variant="h5" gutterBottom fontWeight="bold">
            {isSpanish ? 'Quién está detrás' : "Who's behind this"}
          </Typography>
          <Typography variant="body1" paragraph>
            {isSpanish
              ? 'Me llamo Augusto Balarezo. Vivo en Lima y me dedico a ordenar negocios que se volvieron un caos — gente que domina su oficio pero a la que nadie le enseñó a gestionar.'
              : "My name is Augusto Balarezo. I live in Lima and my work is cleaning up businesses that turned into chaos — people who are masters of their craft but whom nobody ever taught to manage."}
          </Typography>
          <Typography variant="body1" paragraph>
            {isSpanish
              ? 'Haciendo ese trabajo aprendí algo que no esperaba: el problema casi nunca empieza en el negocio. Empieza antes, en la relación de la persona con sus propios números. Quien no sabe cuánto tiene en su cuenta tampoco lo sabe en su empresa, y al revés no funciona — ordenar la empresa de alguien que no ve su propia plata dura hasta que te vas.'
              : "Doing that work I learned something I didn't expect: the problem almost never starts in the business. It starts earlier, in a person's relationship with their own numbers. Someone who doesn't know what's in their account doesn't know it in their company either — and it doesn't work the other way around: cleaning up the business of someone who can't see their own money lasts until you leave."}
          </Typography>
          <Typography variant="body1" paragraph>
            {isSpanish
              ? 'Por eso PCM existe y por eso es personal. No es una versión chica de un software contable: es la parte que viene primero, la que le sirve a cualquiera tenga o no un negocio.'
              : "That's why PCM exists and why it's personal. It isn't a small version of accounting software: it's the part that comes first, the part that helps anyone, business or not."}
          </Typography>
          <Typography variant="body1">
            {isSpanish
              ? 'Y está pensado para el dinero de una casa, no solo de una persona: el sueldo, el mercado, las cuotas del colegio, la tarjeta que usan los dos, el préstamo del auto. Esa es la plata que de verdad quita el sueño.'
              : "And it's built for a household's money, not just one person's: the salary, the groceries, school fees, the card you both use, the car loan. That's the money that actually keeps people up at night."}
          </Typography>
        </Paper>

        {/* La metodología */}
        <Paper elevation={2} sx={{ p: { xs: 3, md: 5 }, mb: 6, borderRadius: 2 }}>
          <Typography variant="h5" gutterBottom fontWeight="bold">
            {isSpanish ? 'Por qué se llama PCM' : 'Why it’s called PCM'}
          </Typography>
          <Typography variant="body1" paragraph>
            {isSpanish
              ? 'Planificación, Control y Monitoreo. No es un nombre de marketing: es el orden en que hay que hacer las cosas. Saltarse uno es la razón más común por la que la gente abandona una app de finanzas a las dos semanas.'
              : "Planning, Control and Monitoring. It's not a marketing name: it's the order in which things have to be done. Skipping one is the most common reason people abandon a finance app after two weeks."}
          </Typography>
          <Divider sx={{ my: 3 }} />
          <Grid container spacing={3}>
            {pilares.map((p) => {
              const Icon = p.icon;
              return (
                <Grid item xs={12} md={4} key={p.letra}>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1 }}>
                    <Typography variant="h4" fontWeight="bold" sx={{ color: goldColors.main }}>
                      {p.letra}
                    </Typography>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {p.titulo}
                    </Typography>
                    <Icon fontSize="small" sx={{ color: goldColors.main, ml: 'auto' }} />
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {p.desc}
                  </Typography>
                </Grid>
              );
            })}
          </Grid>
        </Paper>

        {/* Cómo trabajo */}
        <Paper elevation={2} sx={{ p: { xs: 3, md: 5 }, mb: 6, borderRadius: 2 }}>
          <Typography variant="h5" gutterBottom fontWeight="bold">
            {isSpanish ? 'Cómo trabajo' : 'How I work'}
          </Typography>
          <Typography variant="body1" paragraph>
            {isSpanish
              ? 'Con mis clientes de consultoría tengo una regla que también vale acá: yo no ejecuto por ti. Te muestro exactamente qué hacer y superviso que se haga bien. El método funciona porque tú te apropias del cambio — no porque yo lo haga por ti y me lo lleve cuando me voy.'
              : "With my consulting clients I have a rule that applies here too: I don't do it for you. I show you exactly what to do and I make sure it's done right. The method works because you own the change — not because I do it for you and take it with me when I leave."}
          </Typography>
          <Typography variant="body1" paragraph>
            {isSpanish
              ? 'PCM está construido con esa misma idea. No promete que una inteligencia artificial va a arreglar tus finanzas mientras duermes. Te da los números claros, en el momento en que los necesitas, para que las decisiones —que siguen siendo tuyas— dejen de ser una apuesta.'
              : "PCM is built on that same idea. It doesn't promise that some artificial intelligence will fix your finances while you sleep. It gives you clear numbers, when you need them, so that the decisions — which are still yours — stop being a gamble."}
          </Typography>
          <Typography variant="body1">
            {isSpanish
              ? 'Tampoco te va a decir que dejes el café. La meta no es que gastes menos: es que sepas, el día 5, cómo vas a llegar al día 30. Cuando eso está claro, gastar deja de dar culpa y la conversación de la casa sobre la plata se vuelve mucho más tranquila.'
              : "It also won't tell you to give up coffee. The goal isn't for you to spend less: it's for you to know, on the 5th, how you'll get to the 30th. Once that's clear, spending stops feeling like guilt and the money conversation at home gets a lot calmer."}
          </Typography>
        </Paper>

        {/* Límite explícito: qué NO es PCM */}
        <Paper elevation={2} sx={{ p: { xs: 3, md: 5 }, mb: 6, borderRadius: 2 }}>
          <Typography variant="h5" gutterBottom fontWeight="bold">
            {isSpanish ? 'Lo que PCM no es' : "What PCM isn't"}
          </Typography>
          <Typography variant="body1" paragraph>
            {isSpanish
              ? 'PCM no lleva la contabilidad de una empresa. No emite comprobantes, no arma estados financieros ni cuadra un libro de partida doble, y no está pensado para que registres ahí las ventas de tu negocio. Meter las dos cosas en un mismo lugar es precisamente el desorden que hay que evitar.'
              : "PCM does not do a company's accounting. It doesn't issue invoices, build financial statements or balance a double-entry ledger, and it isn't meant for recording your business's sales. Mixing both in one place is exactly the disorder to avoid."}
          </Typography>
          <Typography variant="body1" paragraph>
            {isSpanish
              ? 'Si tienes un negocio, sus finanzas van en CapitalOptima, que es otra herramienta y está hecha para eso. Y si el desorden es más grande que cualquier software —operaciones, procesos, equipo— eso es consultoría, y la hago por separado en Darwin Optima.'
              : 'If you run a business, its finances belong in CapitalOptima, a separate tool built for that. And if the disorder is bigger than any software — operations, processes, team — that is consulting, and I do it separately at Darwin Optima.'}
          </Typography>
          <Typography variant="body1">
            {isSpanish
              ? 'Una aclaración sobre lo familiar: PCM lleva las finanzas de tu hogar, pero hoy la cuenta es de una sola persona. No hay usuarios separados para la pareja ni permisos por miembro — en la práctica, uno lleva las cuentas de la casa y comparte lo que quiera mostrar. Si te hace falta que ambos entren por su lado, dímelo: eso define qué se construye después.'
              : "One clarification about the family part: PCM handles your household's finances, but today the account belongs to one person. There are no separate logins for a partner or per-member permissions — in practice, one person keeps the household books and shares whatever they want to show. If you need both of you to log in separately, tell me: that's what decides what gets built next."}
          </Typography>
        </Paper>

        {/* Honestidad sobre la etapa */}
        <Paper
          variant="outlined"
          sx={{ p: { xs: 3, md: 4 }, mb: 6, borderRadius: 2, borderStyle: 'dashed' }}
        >
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            {isSpanish ? 'En qué etapa estamos' : 'Where we are right now'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isSpanish
              ? 'PCM es un producto joven y hoy lo llevo yo, desde Lima. Eso tiene una desventaja y una ventaja: no somos una empresa grande con centro de soporte, pero cuando escribes, te responde la persona que construyó la aplicación. Si algo te falta o algo te molesta, dímelo — esa es, literalmente, la lista de pendientes.'
              : "PCM is a young product and today I run it myself, from Lima. That has a downside and an upside: we're not a large company with a support center, but when you write in, the person who built the app is the one who answers. If something is missing or something bothers you, tell me — that is, literally, the to-do list."}
          </Typography>
        </Paper>

        {/* Cierre */}
        <Box sx={{ textAlign: 'center', py: 4, bgcolor: 'background.default', borderRadius: 2 }}>
          <Typography variant="h5" gutterBottom>
            {isSpanish ? '¿Conversamos?' : 'Shall we talk?'}
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph sx={{ maxWidth: 620, mx: 'auto' }}>
            {isSpanish
              ? 'Puedes empezar a usar PCM por tu cuenta con 14 días de prueba, o agendar una sesión conmigo para revisar tu situación y salir con un plan concreto.'
              : 'You can start using PCM on your own with a 14-day trial, or book a session with me to go over your situation and leave with a concrete plan.'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap', mt: 2 }}>
            <Button variant="contained" color="primary" component={RouterLink} to="/register">
              {isSpanish ? 'Probar PCM gratis' : 'Try PCM free'}
            </Button>
            <Button variant="outlined" color="primary" component={RouterLink} to="/consultant">
              {isSpanish ? 'Agendar una consulta' : 'Book a consultation'}
            </Button>
            <Button variant="text" color="primary" href="mailto:augusto@darwinoptima.com">
              {isSpanish ? 'Escríbeme' : 'Email me'}
            </Button>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default NosotrosPage;
